<template>
  <div class="chart_container">
    <div class="horizontal_bar_summary_container">
      <div class="bar_chart_container_title">{{ kindText }}</div>
      <v-img
        src="../../assets/images/expand.png"
        class="clickable"
        max-height="36px"
        max-width="36px"
        v-if="expandIconVisible"
        @click="dialog = true"
      >
      </v-img>
      <v-dialog v-model="dialog" style="border-radius: 10px" max-width="700px">
        <template>
          <v-card
            style="
              border-radius: 10px;
              padding: 10px;
              background-color: #ffffff;
            "
            elevation="0"
          >
            <div class="dialog_header">
              <div class="dialog_title">{{ kindText }}</div>
              <div class="clickable" @click="onClickClose">
                <i
                  class="material-icons"
                  style="margin-left: 10px; color: #878a8c"
                  >&#xE5CD;</i
                >
              </div>
            </div>
            <v-divider style="margin=10px"></v-divider>
            <ModalHorizontalBarChart
              v-bind:itemWeightInGram="itemWeightInGram"
              v-bind:receivedData="dataForModal"
              v-bind:mode="modalMode"
              v-bind:barColors="getColorSet()"
            />
          </v-card>
        </template>
      </v-dialog>
    </div>
    <div v-if="dataCount > 0">
      <div class="horizontal_bar_chart">
        <Bar
          :chart-options="chartOptions"
          :chart-data="chartData"
          :chart-id="chartId"
          :dataset-id-key="datasetIdKey"
          :plugins="plugins"
          :css-classes="cssClasses"
          :styles="styles"
          :width="width"
          :height="height"
        />
      </div>
      <div class="chart_unit_text">*kg</div>
    </div>
    <div v-else class="horizontal_bar_chart" style="height: 200px">
      <DataNotFound />
    </div>
  </div>
</template>

<script>
import ModalHorizontalBarChart from './ModalHorizontalBarChart'
import { Bar } from 'vue-chartjs/legacy'
import ChartJsPluginDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import custom_tooltip from '../../mixins/custom_tooltip'
import utils from '../../mixins/utils'
import DataNotFound from '../common/DataNotFound'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ChartJsPluginDataLabels)
export default {
  name: 'CO2SummaryHorizontalBarChart',
  components: { Bar, ModalHorizontalBarChart, DataNotFound },
  props: {
    receivedData: {
      type: Object,
      default: () => {}
    },
    itemWeightInGram: {
      type: Object,
      default: () => {}
    },
    kindText: {
      type: String,
      default: ''
    },
    chartId: {
      type: String,
      default: 'horizontal-bar-chart'
    },
    datasetIdKey: {
      type: String,
      default: 'label'
    },
    width: {
      type: Number,
      default: 415
    },
    height: {
      type: Number,
      default: 176
    },
    cssClasses: {
      default: '',
      type: String
    },
    styles: {
      type: Object,
      default: () => {}
    },
    plugins: {
      type: Array,
      default: () => []
    },
    expandIconVisible: {
      type: Boolean,
      default: true
    },
    modalMode: {
      type: Number,
      default: 0
    },
    colorSet: {
      type: Number,
      default: 0
    },
    fixedPoint: {
      type: Number,
      default: null
    }
  },
  mixins : [custom_tooltip, utils],
  methods : {
    onClickClose() {
        this.dialog = false;
    },
    getColorSet() {
      if(this.colorSet == 0) {
        return ['#A76262', '#B87D7D', '#CA9898', '#DCB3B3', '#EDCDCD']
      }
      return ['#FFAF2A', '#FFC25D', '#FFCD7B', '#FFD999', '#FFE0AC']
    }
  },
  watch: {
    receivedData: {
      handler: function() {
        const map = new Map(Object.entries(this.receivedData))
        this.dataCount = map.size
        let sortedByValueDsc = new Map([...map].sort((a, b) => a[1] - b[1]).reverse());
        let newLabels = []
        let newLabelsForTooltip = []
        let newData = []

        this.dataForModal = Object.fromEntries(sortedByValueDsc)
        sortedByValueDsc.forEach((val, key) => {
          let label = String(key)
          newLabelsForTooltip.push(label)
          label = label.length > 6 ? label.substring(0, 4) + ".." : label
          newLabels.push(label)
          let data = this.fixedPoint ? (Number(val) / 1000).toFixed(this.fixedPoint) : (Number(val) / 1000).toFixed(1)
          newData.push(data)
        });

        if (newData[0] > 0) {
          // データラベルがはみ出てしまうため、maxをあげて右にスペースを作る
          this.chartOptions.scales.x.max = newData[0] * 1.24
        }

        // 5個未満の場合ダミー追加
        for (let i = newLabels.length ; i < 5 ; i++)  {
          newLabels.push("")
          newData.push(0)
        }

        this.chartData = {
          labels: newLabels.slice(0, 5),
          datasets: [{
            data: newData.slice(0, 5),
            backgroundColor: this.getColorSet(),
            borderColor: 'rgba(255, 99, 132, 1)',
            borderSkipped: false,
            borderRadius: 5,
            },
          ]
        }
        this.chartOptions.plugins.tooltip.external = this.getExternalTooltip('kg', '', newLabelsForTooltip, 'translate(0, 20%)')
      },
      immediate: true
    }
  },
  data() {
    return {
      dialog: false,
      dataForModal: {},
      dataCount: 0,
      chartData: {
        labels: ['アイテム1','アイテム2','アイテム3','アイテム4','アイテム5'],
        datasets: [{
          data: [90, 80, 70, 60, 50],
          backgroundColor: this.getColorSet(),
          borderSkipped: false,
          borderRadius: 5,
          },
        ]
      },
      chartOptions: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        categoryPercentage: 0.6,
        responsive: true,
        scales: {
          x: {
            beginAtZero: true,
            max: 110,
            grid: {
              borderWidth: 0,
              display: false,
              drawOnChartArea: false,
              
            },
            display: false,
          },
          y: {
            beginAtZero: true,
            grid: {
              borderWidth: 0,
              display: false,
            },
          },
        },
        plugins: {
          datalabels: {
            anchor: 'end', // remove this line to get label in middle of the bar
            align: 'end',
            formatter: (val, context) => {
              let fixedPoint = this.fixedPoint ? this.fixedPoint : val < 1.0 ? 2 : 0
              let formattedValue = this.convertToFormattedNumber(val, fixedPoint) + ''
              return this.dataCount <= context.dataIndex ? '' : formattedValue
            },
            labels: {
              value: {
              color: '#343A3F'
              }
            }
          },
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            position: 'average',
            external: this.getExternalTooltip('kg')
          }
        },
      }
    }
  }
}

</script>

<style scoped>
.chart_container {
  width: 437px;
  height: 261px;
  background: #ffffff;
  border: 1px solid rgba(52, 58, 63, 0.1);
  border-radius: 12px;
}

.horizontal_bar_summary_container {
  height: 60px;
  display: flex;
  justify-content: space-between;
  padding: 10px 10px;
  align-items: center;
}

.bar_chart_container_title {
  /* Light/Typography/Body 1 Semibold */

  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  letter-spacing: 0.15px;

  /* Forms/Normal */

  color: #333333;
}

.chart_unit_text {
  font-family: "SF Pro";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  text-align: right;
  margin-right: 12px;
  color: #697077;
}

.horizontal_bar_chart {
  display: flex;
  border-top: solid 1px rgba(52, 58, 63, 0.1);
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.dialog_header {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 10px;
}

.dialog_title {
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  letter-spacing: 0.15px;

  /* Forms/Normal */

  color: #333333;
}

.clickable:hover {
  cursor: pointer;
}
</style>
